import api from "@/services/api";

const state = {
  roles: [],
  permissions: [],
  rolePermissions: [],
  myPermissions: [],
  users: [],
  loading: false,
  permissionsLoaded: false,
  rbacEnabled: false,
};

const getters = {
  permissionGroups: state => {
    const modules = {};
    for (const p of state.permissions) {
      const prefix = p.code.substring(0, p.code.lastIndexOf("."));
      if (!modules[p.module]) {
        modules[p.module] = { name: p.module, prefixes: {} };
      }
      if (!modules[p.module].prefixes[prefix]) {
        const segments = prefix.split("-");
        const displayName = segments.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(" ");
        modules[p.module].prefixes[prefix] = {
          prefix,
          displayName,
          pagePermissions: [],
          actionPermissions: [],
        };
      }
      if (p.type === "page") {
        modules[p.module].prefixes[prefix].pagePermissions.push(p);
        modules[p.module].prefixes[prefix].displayName = p.name.replace(/^View /, "");
      } else {
        modules[p.module].prefixes[prefix].actionPermissions.push(p);
      }
    }
    return Object.values(modules).map(m => ({
      name: m.name,
      items: Object.values(m.prefixes),
    }));
  },
};

const actions = {
  async fetchRoles({ commit }) {
    try {
      const res = await api.get("/rbac/roles");
      commit("SET_ROLES", res.data);
      return res.data;
    } catch (e) {
      console.error("Failed to fetch roles", e);
    }
  },
  async createRole({ dispatch }, body) {
    const res = await api.post("/rbac/roles", body);
    await dispatch("fetchRoles");
    return res.data;
  },
  async updateRole({ dispatch }, { id, body }) {
    await api.put(`/rbac/roles/${id}`, body);
    await dispatch("fetchRoles");
  },
  async deleteRole({ dispatch }, id) {
    await api.deleted(`/rbac/roles/${id}`);
    await dispatch("fetchRoles");
  },
  async fetchPermissions({ commit }) {
    const res = await api.get("/rbac/permissions");
    commit("SET_PERMISSIONS", res.data);
    return res.data;
  },
  async fetchRolePermissions({ commit }, roleId) {
    const res = await api.get(`/rbac/roles/${roleId}/permissions`);
    commit("SET_ROLE_PERMISSIONS", res.data);
    return res.data;
  },
  async saveRolePermissions(_, { roleId, permissionCodes }) {
    await api.put(`/rbac/roles/${roleId}/permissions`, permissionCodes);
  },
  async fetchMyPermissions({ commit }) {
    try {
      const res = await api.get("/rbac/my-permissions");
      commit("SET_MY_PERMISSIONS", res.data);
      commit("SET_PERMISSIONS_LOADED", true);
      return res.data;
    } catch (e) {
      console.error("Failed to fetch my permissions", e);
      commit("SET_PERMISSIONS_LOADED", true);
      return [];
    }
  },
  async fetchRbacEnabled({ commit }) {
    try {
      const res = await api.get("/rbac/enabled");
      commit("SET_RBAC_ENABLED", !!(res.data && res.data.enabled));
      return res.data;
    } catch (e) {
      console.error("Failed to fetch RBAC enabled flag", e);
      commit("SET_RBAC_ENABLED", false);
      return { enabled: false };
    }
  },
  async setRbacEnabled({ commit }, enabled) {
    await api.put(`/rbac/enabled?enabled=${enabled ? "true" : "false"}`, {});
    commit("SET_RBAC_ENABLED", !!enabled);
  },
  async initRbacState({ dispatch }) {
    await Promise.all([dispatch("fetchMyPermissions"), dispatch("fetchRbacEnabled")]);
  },
  async fetchRoleUsers(_, roleId) {
    const res = await api.get(`/rbac/roles/${roleId}/users`);
    return res.data;
  },
  async fetchUsers({ commit }) {
    const res = await api.get("/rbac/users");
    commit("SET_USERS", res.data);
    return res.data;
  },
  async fetchUserRole(_, userId) {
    const res = await api.get(`/rbac/users/${userId}/roles`);
    return res.data;
  },
  async saveUserRole(_, { userId, roleName }) {
    await api.put(`/rbac/users/${userId}/roles?roleName=${encodeURIComponent(roleName)}`);
  },
};

const mutations = {
  SET_ROLES(state, payload) {
    state.roles = payload;
  },
  SET_PERMISSIONS(state, payload) {
    state.permissions = payload;
  },
  SET_ROLE_PERMISSIONS(state, payload) {
    state.rolePermissions = payload;
  },
  SET_MY_PERMISSIONS(state, payload) {
    state.myPermissions = payload;
  },
  SET_PERMISSIONS_LOADED(state, payload) {
    state.permissionsLoaded = payload;
  },
  SET_RBAC_ENABLED(state, payload) {
    state.rbacEnabled = payload;
  },
  SET_USERS(state, payload) {
    state.users = payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
